import GiftCardLayout from '@components/gift-cards-configurator/gift-card-layout/gift-card-layout';
import {
  v2_FallbackProvider as FallbackProvider,
  v2_fetchProduct,
  v2_asProductFallback,
} from '@crocodille/common';
import { AppPage } from '@framework/page';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import { ConfiguratorPageParams } from '@routing';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { assert } from '@crocodille/util';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import * as routing from '@routing';
import PageMeta from '@framework/page-meta';
import { useTranslation } from 'next-i18next';

interface ConfiguratorPageProps {
  readonly branch: string;
  readonly taxon: string;
  readonly product: string;
  readonly fallback: Record<string, unknown>;
}

const ConfiguratorPage: AppPage<ConfiguratorPageProps> = ({
  branch,
  taxon,
  product,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <PageMeta
        pageTitle={t('gift_card_configurator.page_title')}
        description={''}
      />
      <GiftCardLayout branch={branch} taxon={taxon} product={product} />{' '}
    </>
  );
};

ConfiguratorPage.getLayout = (page, pageProps) => {
  return getAppLayoutWithCart(
    <FallbackProvider fallback={pageProps.fallback}>{page}</FallbackProvider>,
  );
};

export const getStaticPaths: GetStaticPaths<
  ConfiguratorPageParams & ParsedUrlQuery
> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
export const getStaticProps: GetStaticProps<
  ConfiguratorPageProps,
  ConfiguratorPageParams & ParsedUrlQuery
> = async (context) => {
  const branch = context.params?.branch;
  const taxon = context.params?.taxon;
  const slug = context.params?.slug;

  assert(
    typeof branch === 'string' &&
      typeof taxon === 'string' &&
      typeof slug === 'string',
  );

  const app = getApp(context);

  const [translationProps, product] = await Promise.all([
    getServerSideTranslationProps(app, ['messages']),
    v2_fetchProduct(app, {
      branchCode: branch,
      slug,
    }),
  ]);

  if (product?.configurationStyle !== 'gift_card') {
    return {
      redirect: {
        permanent: false,
        destination: routing.localized(
          app.locale,
          routing.products({ taxon, product: slug }),
        ),
      },
    };
  }

  if (!product) {
    return {
      notFound: true,
    };
  }

  const fallback = {
    ...v2_asProductFallback(app, { branchCode: branch, slug }, product),
  };

  return {
    revalidate: 60 * 5, // 5 minutes
    props: {
      ...translationProps,
      branch,
      taxon,
      product: slug,
      fallback,
    },
  };
};

export default ConfiguratorPage;
